import {
  trackDubGenerateCompleted,
  trackDubGenerateFailed,
  trackDubGenerateStarted,
} from '@src/analytics/EventTracker';
import {
  PrimaryButton,
  SecondaryButton,
} from '@src/components/common/dialogStyles';
import Spinner from '@src/components/common/Spinner';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { useAnchoredPopoverPlacement } from '@src/components/common/useAnchoredPopoverPlacement';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import SimpleLoadingSpinner from '@src/components/SimpleLoadingSpinner';
import { useOutsideAlerter } from '@src/components/transcript/useClickOutside';
import { useExclusiveTranslationPopover } from '@src/components/transcript/useExclusiveTranslationPopover';
import { estimateDubGenerateMs } from '@src/config/processingEstimates';
import { useSpeakerManagementService } from '@src/hooks/useSpeakerManagementService';
import { DubbingApiError } from '@src/repositories/DubbingRepository';
import { isOnePerson } from '@src/services/dubbing/directionPrefill';
import { resolveAssignedSpeakers } from '@src/services/SpeakerManagementService';
import { useDubbingStore, useTranscription } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CloseIcon from '@src/svgs/CloseIcon';
import { normalizeLangKey } from '@src/utility/bcp47';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { LogManager } from '@src/utility/Logger';
import { getSpeakerNameFromLabel } from '@src/utility/speakerUtils';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import DubDirectionField, {
  DUB_DIRECTION_PORTAL_ATTR,
} from './DubDirectionField';
import ManageAiVoicesModal from './ManageAiVoicesModal';
import RedubSpeakerRow from './RedubSpeakerRow';
import { useDubDirectionState } from './useDubDirectionState';
import { useDubPassagePlayback } from './useDubPassagePlayback';
import { useVoiceActivation } from './useVoiceActivation';

import type { AnchoredPopoverPlacement } from '@src/components/common/useAnchoredPopoverPlacement';
import type { ReDubSelectionRequest } from '@src/components/transcript/hooks/useSpanReplace';
import type { DubGenerateResult } from '@src/repositories/DubbingRepository';
import type { SpeakerWithColor } from '@src/services/SpeakerManagementService';
import type { Language } from '@src/types.ts/language';

const logger = LogManager.getLogger('aiVoices:RedubSelectionPopover');
const TOOLTIP_ID = 'redub-selection-tooltip';

// "Alice", "Alice and Bob", "Alice, Bob and Carol".
const listNames = (names: string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

const pluralLines = (n: number) => (n === 1 ? '1 line' : `${n} lines`);

/** PLAT-12080 — the run as the popover shows it. */
type RunPhase =
  | { kind: 'idle' }
  | { kind: 'running' }
  | { kind: 'landed' }
  | { kind: 'failed'; message: string };

export interface RedubSelectionPopoverProps {
  storyId: string;
  /** The dubbed language the selection is in (BCP-47). */
  targetLanguage: string;
  /** Workspace supported languages (for the caption + the DI19 pin key). */
  supportedLanguages: Language[];
  /** What the selection menu handed up: the lines, their speakers, the anchor. */
  request: ReDubSelectionRequest;
  /** The room the dub has on the story's timeline, in milliseconds. */
  availableDurationMs?: number;
  onClose: () => void;
  /** The run landed: the host refreshes what plays. Fires even if closed. */
  onSuccess?: (result: DubGenerateResult) => void;
  /**
   * PLAT-12024 — bring the re-dubbed passage back into view and play it. When
   * given, the "dub updated" toast carries a "Show Passage" action that calls
   * it with the run's lines; without it the toast has no action.
   */
  onRevealPassage?: (segmentIds: string[]) => void;
}

/**
 * PLAT-12022 — re-dub a highlighted passage, asked beside the passage.
 *
 * "Re-dub AI Voice" on a selection used to open the whole-story dub modal in
 * section mode: every speaker in the story, each with a locked voice picker,
 * Keep Original Music, and the direction field under all of it. A selected
 * passage belongs to one speaker (two, when it crosses a change), so this
 * popover shows those speakers and nothing else, asks only how the words
 * should be delivered, and re-records exactly the selected lines.
 *
 * What it shares with the modal: the direction block (`useDubDirectionState`
 * + `DubDirectionField`) and the run itself (`DubbingStore.startRun`). What
 * it leaves out: the other speakers, the voice pickers, the music option,
 * the first-dub and fill-gaps modes. The speaker rows are read-only
 * (`RedubSpeakerRow`): this popover adjusts how a passage is delivered, not
 * who says it — the run reuses each speaker's current voice, and the rows
 * say where voices are changed.
 *
 * The speaker leads (PLAT-12078): the row names who says the lines and with
 * which voice, and its one play sounds the dub's CURRENT take of exactly the
 * selected lines — hear it as it is, direct, Re-dub. Nothing in the popover
 * repeats the transcript: the highlighted words sit right beside it. A
 * selection across two speakers is refused in words, since one direction
 * cannot serve two voices (per-speaker delivery is PLAT-12046).
 *
 * Anchored to the selection's first word and portaled to `document.body`
 * (design system, "Anchored Popovers"): placed by its measured height, one
 * translation popover at a time, click-away and Escape close it. It stays
 * open through a re-dub and after the take lands (PLAT-12080): listen,
 * adjust, re-dub again, without a trip through the page.
 */
const RedubSelectionPopover = observer(
  ({
    storyId,
    targetLanguage,
    supportedLanguages,
    request,
    availableDurationMs,
    onClose,
    onSuccess,
    onRevealPassage,
  }: RedubSelectionPopoverProps) => {
    const dubbingStore = useDubbingStore();
    const videoCreator = useVideoCreatorStore();
    const speakerService = useSpeakerManagementService();
    const transcriptionStore = useTranscription();
    const { organization } = useVoiceActivation();
    const wrapperRef = useRef<HTMLDivElement>(null);

    const placement = useAnchoredPopoverPlacement(
      request.anchorElement,
      wrapperRef,
      {
        width: POPOVER_WIDTH,
        estimatedHeight: POPOVER_ESTIMATED_HEIGHT,
        // PLAT-12042 — the popover quotes the passage itself, so when the
        // direction controls make it taller than the room beside the words,
        // it covers them and takes the window's height rather than scrolling
        // in a strip.
        coverAnchorWhenScrolling: true,
      },
    );

    // ── Speakers: the selection's, resolved the way the modal resolves them ──
    const [speakers, setSpeakers] = useState<SpeakerWithColor[]>([]);
    const [labelToDatoId, setLabelToDatoId] = useState<Record<string, string>>(
      {},
    );
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selections, setSelections] = useState<Record<string, string>>({});
    const [manageSpeaker, setManageSpeaker] = useState<SpeakerWithColor | null>(
      null,
    );
    const [reloadTokens, setReloadTokens] = useState<Record<string, number>>(
      {},
    );

    // PLAT-12080 — the run as this popover sees it. It stays open through
    // the run and after the take lands, so the person can hear the new take
    // and stack the next change; a take that lands bumps `takeToken`, which
    // re-reads the passage play and re-seeds the controls from that take.
    const [phase, setPhase] = useState<RunPhase>({ kind: 'idle' });
    const [takeToken, setTakeToken] = useState(0);
    // Closing mid-run must not cancel the run (it lives in the store); when
    // it lands with the popover gone, the toast reports it as before.
    // Set again on every setup, not only at first render: a dev hot reload
    // re-runs this effect's cleanup and setup without remounting, and a ref
    // left false made a landed run read as "closed" — the toast fired over
    // the open popover and its footer kept spinning.
    const mountedRef = useRef(true);
    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      if (organization?.id) void dubbingStore.hydrate(organization.id);
    }, [organization?.id, dubbingStore]);

    // The same fetch and the same definition of "assigned" as the dub modal
    // (PLAT-11583), then narrowed to the labels the selection named.
    useEffect(() => {
      let cancelled = false;
      setLoading(true);
      void speakerService.fetchSpeakersForStory(storyId).then(result => {
        if (cancelled) return;
        if (!result.ok) {
          setLoadError(result.error.message);
          setLoading(false);
          return;
        }
        const elements =
          transcriptionStore?.getOriginalTranscription()?.elements ?? [];
        const { assigned, labelToSpeakerId } = resolveAssignedSpeakers({
          storyId,
          storySpeakers: result.value.storySpeakers,
          mappings: result.value.mappings,
          transcriptElements: elements,
        });
        const wantedIds = new Set(
          request.speakerLabels
            .map(label => labelToSpeakerId[label])
            .filter((id): id is string => !!id),
        );
        const scoped: Record<string, string> = {};
        for (const label of request.speakerLabels) {
          const id = labelToSpeakerId[label];
          if (id) scoped[label] = id;
        }
        setSpeakers(assigned.filter(s => wantedIds.has(s.id)));
        setLabelToDatoId(scoped);
        setLoading(false);
      });
      return () => {
        cancelled = true;
      };
    }, [storyId, speakerService, transcriptionStore, request.speakerLabels]);

    const runLanguage = useMemo(
      () => supportedLanguages.find(l => l.value === targetLanguage) ?? null,
      [supportedLanguages, targetLanguage],
    );
    const languageCaption =
      runLanguage?.caption ?? formatLanguageCaption(targetLanguage);

    const handleSelect = useCallback(
      (speakerId: string, voiceId: string | undefined) => {
        setSelections(prev => {
          const next = voiceId ?? '';
          if (prev[speakerId] === next) return prev;
          return { ...prev, [speakerId]: next };
        });
      },
      [],
    );

    // label → aiVoiceId for the request: only the selection's speakers, so the
    // run voices the lines asked for and nobody else (the engine skips any
    // speaker the map does not name).
    const voiceBySpeaker = useMemo(() => {
      const map: Record<string, string> = {};
      for (const [label, datoId] of Object.entries(labelToDatoId)) {
        const voiceId = selections[datoId];
        if (voiceId) map[label] = voiceId;
      }
      return map;
    }, [labelToDatoId, selections]);
    const dubbableCount = Object.keys(voiceBySpeaker).length;

    // A label with no speaker behind it (never assigned, or assigned after
    // the dub was made) cannot be re-dubbed from here: a voice belongs to a
    // speaker. Named, so the person knows which passage this is about.
    const unassignedNames = useMemo(
      () =>
        request.speakerLabels
          .filter(label => !labelToDatoId[label])
          .map(getSpeakerNameFromLabel),
      [request.speakerLabels, labelToDatoId],
    );
    const speakersMissingVoice = useMemo(
      () => speakers.filter(s => !selections[s.id]).map(s => s.name),
      [speakers, selections],
    );

    // ── Direction: the shared block ──
    const directionState = useDubDirectionState({
      storyId,
      language: targetLanguage,
      segmentIds: request.segmentIds,
      scopeToSpanSpeakers: true,
      labelToDatoId,
      selections,
      speakers,
      reloadToken: takeToken,
    });
    const { direction, blocked: directionBlocked } = directionState;

    // PLAT-12078 — one speaker only: one direction cannot serve two voices,
    // and the popover says so rather than sending a run that serves neither.
    // Counted by PERSON, not by transcript label: one person can carry two
    // labels (both mapped to them), and their lines take one voice and one
    // direction. A label with no person behind it counts as its own (PLAT-12198).
    const twoSpeakers = !isOnePerson(
      request.speakerLabels,
      label => labelToDatoId[label],
    );

    // The dub's current take of these lines, on their own: what the speaker
    // row's play sounds.
    const passage = useDubPassagePlayback({
      storyId,
      language: targetLanguage,
      segmentIds: request.segmentIds,
      reloadToken: takeToken,
    });

    const activeRun = dubbingStore.activeRun;
    const running =
      activeRun !== null &&
      activeRun.storyId === storyId &&
      normalizeLangKey(activeRun.targetLanguage) ===
        normalizeLangKey(targetLanguage);

    const disabledReason = loading
      ? null
      : loadError
        ? loadError
        : twoSpeakers
          ? 'Select words from one speaker to re-dub them.'
          : unassignedNames.length > 0
            ? `${listNames(unassignedNames)} ${
                unassignedNames.length === 1 ? 'has' : 'have'
              } no speaker assigned, so there is no AI Voice to re-dub with. Assign one on the Speakers panel first.`
            : speakersMissingVoice.length > 0
              ? `${listNames(speakersMissingVoice)} still ${
                  speakersMissingVoice.length === 1 ? 'needs' : 'need'
                } an AI Voice for ${languageCaption}.`
              : running
                ? `A ${languageCaption} dub is already generating.`
                : null;
    const canGenerate =
      !loading && !running && dubbableCount > 0 && disabledReason === null;

    // ── Dismissal: click-away, Escape, another translation popover ──
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    // Click-away closes, unless the click is in the Manage AI Voices modal
    // this popover opened (a sibling portal, so it reads as "outside"), or
    // in a panel the direction field portals out (the marker list,
    // PLAT-12074): that mousedown used to close the popover before the
    // click that writes the marker could fire.
    useOutsideAlerter(wrapperRef, event => {
      if (manageSpeaker) return;
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest(`[${DUB_DIRECTION_PORTAL_ATTR}]`)) return;
      onCloseRef.current();
    });
    useExclusiveTranslationPopover(onClose);
    // Escape closes from anywhere on the page, not only with focus inside:
    // the popover opens from a mouse selection, so focus is usually still on
    // the transcript. Capture phase, so the review workspace's own
    // Escape-to-close (which listens on document too) does not also fire.
    // While Manage AI Voices sits on top, Escape belongs to it; so does it
    // while a panel the direction field portals out is open (the tag picker,
    // Auto-tag's menu), which closes on its own Escape.
    useEffect(() => {
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Escape' || manageSpeaker) return;
        if (document.querySelector(`[${DUB_DIRECTION_PORTAL_ATTR}]`)) return;
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
      };
      document.addEventListener('keydown', onKeyDown, true);
      return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [manageSpeaker]);

    // ── Generate: the modal's span path, narrowed to the selection ──
    const handleGenerate = useCallback(async () => {
      if (!canGenerate || directionBlocked) return;
      trackDubGenerateStarted({
        story_id: storyId,
        language: targetLanguage,
        speaker_count: dubbableCount,
        regenerate: true,
        scope: 'span',
        segment_count: request.segmentIds.length,
      });
      // PLAT-12198 — a selection records a few lines, so the whole-video
      // estimate (minutes, for a run that takes seconds) does not apply and
      // the popover names no time. The page's run chip still needs an ETA
      // for its bar: the estimator's fixed base, with no per-second term.
      const etaMs = estimateDubGenerateMs({
        videoDurationSec: 0,
        speakerCount: dubbableCount,
      });
      setPhase({ kind: 'running' });
      try {
        // PLAT-12080 — the run starts and the popover stays: the footer
        // shows the run, the take lands here, and the next change is one
        // press away. (PLAT-11688 had it close and leave the page's run
        // chip to report; that chip still runs for whoever is not looking
        // here.) `startRun` registers the run synchronously.
        const pending = dubbingStore.startRun(
          {
            storyId,
            targetLanguage,
            voiceBySpeaker,
            segmentIds: request.segmentIds,
            regenerate: true,
            availableDurationMs,
            ...(direction ? { direction } : {}),
          },
          { languageCaption, etaMs },
        );
        const result = await pending;
        trackDubGenerateCompleted({
          story_id: storyId,
          language: targetLanguage,
          run_id: result.runId,
          synthesized_count: result.coverage.synthesized.length,
          reused_count: result.coverage.reused.length,
          skipped_count: result.coverage.skipped.length,
          failed_count: result.coverage.failed.length,
        });
        onSuccess?.(result);
        if (result.coverage.failed.length > 0) {
          const message = `${languageCaption} dub finished, but ${pluralLines(
            result.coverage.failed.length,
          )} couldn’t be dubbed.`;
          if (mountedRef.current) setPhase({ kind: 'failed', message });
          else videoCreator.showToast({ state: 'error', message });
          return;
        }
        // Open, the popover also shows the landing: the new take is one
        // press away on the speaker row and the controls now hold what it
        // was asked. The toast says it either way (PLAT-12198): it is the one
        // signal shared with every other dub run, and closed before the
        // landing it is the only one. Its action (PLAT-12024) takes the
        // person to the words and plays them.
        if (mountedRef.current) {
          setTakeToken(t => t + 1);
          setPhase({ kind: 'landed' });
        }
        const landedIds = [...request.segmentIds];
        videoCreator.showToast({
          state: 'success',
          message: `${languageCaption} dub updated`,
          ...(onRevealPassage
            ? {
                action: {
                  label: 'Show Passage',
                  onClick: () => onRevealPassage(landedIds),
                },
              }
            : {}),
        });
      } catch (err) {
        if (isAbortError(err)) {
          if (mountedRef.current) setPhase({ kind: 'idle' });
          return;
        }
        const info = extractErrorInfo(err);
        logger.error('Selection re-dub failed', info);
        const isVoiceProviderError =
          err instanceof DubbingApiError && err.code === 'VOICE_PROVIDER_ERROR';
        const message = isVoiceProviderError
          ? 'There’s a problem with the voice provider. Please contact support.'
          : info.errorMessage || 'Dub generation failed. Try again.';
        if (mountedRef.current) {
          setPhase({ kind: 'failed', message });
        } else {
          videoCreator.showToast({
            state: 'error',
            message: `${languageCaption} dub failed. ${message}`,
          });
        }
        trackDubGenerateFailed({
          story_id: storyId,
          language: targetLanguage,
          error_message: info.errorMessage || 'unknown',
        });
      }
    }, [
      onRevealPassage,
      canGenerate,
      directionBlocked,
      storyId,
      targetLanguage,
      dubbableCount,
      request.segmentIds,
      dubbingStore,
      voiceBySpeaker,
      availableDurationMs,
      direction,
      languageCaption,
      onSuccess,
      videoCreator,
    ]);

    return ReactDOM.createPortal(
      <Wrapper
        ref={wrapperRef}
        placement={placement}
        role="dialog"
        aria-label="Redub Selection"
        data-testid="redub-selection-popover"
        // PLAT-12044 — while Manage AI Voices is up, this popover steps out
        // of the way rather than painting over the modal: at rest the
        // popover's tier (above the clip-preview stack) is above the shared
        // modal backdrop's, and only the review's clip-preview entry lifts
        // both into tiers where the modal wins. The modal is a sibling portal,
        // so hiding this subtree leaves it alone; the popover returns, with
        // the speaker's voices re-read, when the modal closes.
        $hidden={manageSpeaker !== null}
        aria-hidden={manageSpeaker !== null || undefined}
        data-hidden={manageSpeaker !== null ? 'true' : undefined}
      >
        <Header>
          <div className="row">
            <h2 className="title">Redub Selection</h2>
            <HeaderClose type="button" onClick={onClose} aria-label="Close">
              <CloseIcon width={12} height={12} />
            </HeaderClose>
          </div>
          <div className="row">
            <p className="meta">
              {languageCaption} · {pluralLines(request.segmentIds.length)}
            </p>
            {/* PLAT-12198 — the exit to Manage AI Voices sits on the meta
                line rather than a note of its own under the rows, so the
                direction card starts higher. The row already says the voice
                was picked for the speaker; the link names where to change it. */}
            {!loading && speakers.length > 0 && (
              <ManageLink
                type="button"
                className="manage"
                onClick={() =>
                  setManageSpeaker(
                    speakers.find(s => !selections[s.id]) ?? speakers[0],
                  )
                }
              >
                Manage voices →
              </ManageLink>
            )}
          </div>
        </Header>

        <Speakers>
          {loading ? (
            <LoadingRow>
              <SimpleLoadingSpinner />
            </LoadingRow>
          ) : (
            speakers.map(speaker => (
              <RedubSpeakerRow
                key={speaker.id}
                speaker={speaker}
                language={runLanguage}
                reloadToken={reloadTokens[speaker.id] ?? 0}
                onSelect={handleSelect}
                // PLAT-12078 — the row's play sounds the current take.
                takePlay={passage}
              />
            ))
          )}
        </Speakers>
        {/* PLAT-12051 — said in words, not only on a hover: a line the dub
            never recorded, or whose take was superseded after its words
            changed, has nothing to play; the row's play is the voice's sample
            instead, and Re-dub still records the line afresh. */}
        {passage.state === 'unavailable' && (
          <Reason role="status" data-testid="redub-passage-unavailable">
            No recording of these lines in the dub yet, so the play is the
            voice’s sample.
          </Reason>
        )}

        {!twoSpeakers && (
          <DubDirectionField
            {...directionState.fieldProps}
            disabled={running || loading}
            // PLAT-12042 — the direction covers the selection's one speaker,
            // not "all speakers": the title names who.
            scopeLabel={speakers[0]?.name ?? 'this speaker'}
            defaultOpen
            flush
          />
        )}

        {disabledReason && phase.kind !== 'running' && (
          <Reason role="status" data-testid="redub-disabled-reason">
            {disabledReason}
          </Reason>
        )}
        {/* PLAT-12080 — what the last run did, said here rather than in a
            toast, since the popover is open to hear it. */}
        {phase.kind === 'landed' && (
          <Reason role="status" data-testid="redub-landed">
            New take landed. Press play to hear it, or change the direction and
            re-dub again.
          </Reason>
        )}
        {phase.kind === 'failed' && (
          <FailedLine role="alert" data-testid="redub-failed">
            {phase.message}
          </FailedLine>
        )}
        {/* Before the footer, not after: the footer must be the column's
            last child, or the column's gap opens a strip under the pinned
            footer through which scrolled content shows (PLAT-12045). */}
        <Tooltip id={TOOLTIP_ID} style={MODAL_TOOLTIP_STYLE} />
        <Footer>
          <SecondaryButton type="button" onClick={onClose}>
            {phase.kind === 'landed' ? 'Done' : 'Cancel'}
          </SecondaryButton>
          {phase.kind === 'running' ? (
            // The run, in the slot Re-dub holds: a busy pill, like the
            // split button's. Cancel still closes the popover; the run goes
            // on in the store and the toast reports it then.
            <RunningPill aria-busy="true" data-testid="redub-selection-running">
              <Spinner size="sm" />
              Re-dubbing…
            </RunningPill>
          ) : (
            <PrimaryButton
              type="button"
              onClick={() => void handleGenerate()}
              disabled={!canGenerate || directionBlocked}
              data-testid="redub-selection-generate"
            >
              {phase.kind === 'landed' ? 'Re-dub Again' : 'Re-dub'}
            </PrimaryButton>
          )}
        </Footer>

        {manageSpeaker && (
          <ManageAiVoicesModal
            isOpen
            onClose={() => {
              const managedId = manageSpeaker.id;
              setReloadTokens(prev => ({
                ...prev,
                [managedId]: (prev[managedId] ?? 0) + 1,
              }));
              setManageSpeaker(null);
            }}
            speakerId={manageSpeaker.id}
            speakerName={manageSpeaker.name}
            speakerHeadshotUrl={manageSpeaker.headshots?.[0]?.url ?? null}
            speakerColor={manageSpeaker.color}
            initialLanguageId={runLanguage?.id}
          />
        )}
      </Wrapper>,
      document.body,
    );
  },
);

export default RedubSelectionPopover;

/** Kept in sync with the `Wrapper`'s `width` declaration. Wide enough for a
 *  mapper row's voice column beside the speaker's name. */
const POPOVER_WIDTH = 560;
/** First-paint estimate only — the hook re-places against the real height. */
const POPOVER_ESTIMATED_HEIGHT = 320;

const Wrapper = styled.div.attrs(
  (props: { placement: AnchoredPopoverPlacement; $hidden?: boolean }) => props,
)`
  position: fixed;
  visibility: ${p => (p.$hidden ? 'hidden' : 'visible')};
  left: ${p => p.placement.left}px;
  top: ${p => p.placement.top}px;
  width: ${POPOVER_WIDTH}px;
  max-width: calc(100vw - 16px);
  max-height: ${p => p.placement.maxHeight}px;
  overflow-y: auto;
  /* Body-portaled, so it must clear the clip-preview modal stack on its own;
     inside the Translation Review workspace the lifted context-menu tier
     applies instead (see topLayerZ.ts). */
  z-index: var(
    ${TOP_LAYER_VARS.contextMenu},
    ${CLIP_PREVIEW_Z.translationEditorPopover}
  );

  background: var(--popover);
  color: var(--popover-foreground);
  border: 1px solid var(--border);
  border-radius: var(--radius-input);
  box-shadow: var(--elevation-toast);
  /* No bottom padding: the sticky footer carries it (PLAT-12045), so no
     wrapper padding can sit under the footer for scrolled content to show
     through. */
  padding: 14px 16px 0;

  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;

  /* PLAT-12045 — a scrolling flex column must not let its children shrink:
     with the default flex-shrink the browser squashed the speaker frame (it
     hides its overflow) to fit the max height instead of overflowing, and
     since nothing overflowed the placement hook never saw the real height
     either. Children keep their size; the wrapper scrolls. */
  & > * {
    flex-shrink: 0;
  }
`;

// Title-first header with one stepped meta line and a hairline under it, the
// translation popovers' header grammar (design system, "Translation Field
// Pattern").
const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border);

  .row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .manage {
    margin-left: auto;
  }
  .title {
    margin: 0;
    font-size: var(--text-base);
    font-weight: var(--font-weight-semi-bold);
    color: var(--foreground);
  }
  .meta {
    margin: 0;
    font-size: 11px;
    color: var(--text-muted);
    opacity: 0.8;
  }
`;

const HeaderClose = styled.button`
  margin-left: auto;
  flex-shrink: 0;
  background: transparent;
  border: none;
  padding: var(--spacing-xs);
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
`;

// The speaker rows' frame, on the scroll container, as in the modal.
const Speakers = styled.div`
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
`;

// PLAT-12078 — a quiet link (design system, "Quiet link"): an exit to another
// surface, not an action, so never green. Re-dub is the one Primary here.
const ManageLink = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:hover {
    color: var(--foreground);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 1px var(--focus-ring);
    border-radius: 2px;
  }
`;

const LoadingRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`;

const Reason = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--text-muted);
`;

// PLAT-12080 — a failed run, said where the person is looking.
const FailedLine = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  line-height: 1.4;
  color: var(--destructive);
`;

// The run in Re-dub's slot: the busy pill the split button uses, so a run
// in progress reads the same everywhere.
const RunningPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: var(--btn-h-sm);
  padding: 0 12px;
  border-radius: var(--radius-button);
  background: var(--surface-elevated);
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;

  svg {
    width: 14px;
    height: 14px;
  }
`;

// PLAT-12045 — pinned to the bottom of the scrolling wrapper, over the
// wrapper's own padding, with a hairline above: Cancel / Re-dub are always
// in reach, and a control cut off under the line reads as "scroll", not as
// broken. Sticky inside the wrapper, which is the scroll container.
const Footer = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin: 0 -16px 0;
  padding: 10px 16px 14px;
  background: var(--popover);
  border-top: 1px solid var(--border);
`;
